import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {AnalysisTemplateDataFrameVariableGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {assert} from 'Asserts';
import {
  AnalysisSourcesForConfigOrExecution,
  AnalysisSourcesOrigin,
  DatasetType,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {DatasetCellContent} from 'procedures/shared-components/analysis/DatasetRendering';
import {
  getColumnDefsFromDatasetId,
  getDatasetNameFromId,
  getDatasetTypeFromId,
  isDataPresentForSourceDatasetId,
} from 'procedures/shared-components/analysis/preconfiguredAnalysisUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {SingleAnalysisTemplateTableMapping} from './AnalysisTemplateConfigTypes';
import AnalysisTemplateInputTableConfig from './AnalysisTemplateInputTableConfig';
import AnalysisTemplateSourceTable from './AnalysisTemplateSourceTable';

/** @package Exported for testing */
export function SaveDatasetAssociationButton(props: {
  availableSources: AnalysisSourcesForConfigOrExecution;
  selectedSource: string | null | undefined;
  onSelectDataset: (datasetId: string, datasetType: DatasetType) => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Button
      buttonStyle={ButtonStyle.PRIMARY}
      size={ButtonSize.MEDIUM}
      text="Save"
      disabled={props.selectedSource == null}
      onClick={() => {
        const datasetType = getDatasetTypeFromId(props.selectedSource!, props.availableSources);
        assert(datasetType != null, 'Could not determine dataset type', {
          selectedSource: props.selectedSource,
          availableSources: props.availableSources,
        });
        props.onSelectDataset(props.selectedSource!, datasetType);
        props.onClose();
      }}
      dataTestAttrs={{
        component: 'SaveDatasetAssociationButton',
        element: 'root',
      }}
    />
  );
}

function DatasetPicker(props: {
  availableSources: AnalysisSourcesForConfigOrExecution;
  initialSelectedSource: string | null | undefined;
  analysisSourcesOrigin: AnalysisSourcesOrigin;
  onSelectDataset: (datasetId: string, datasetType: DatasetType) => void;
  onClose: () => void;
}): JSX.Element {
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  return (
    <div
      {...getTestProps({
        component: 'DatasetPicker',
        element: 'root',
      })}
    >
      <div className="u-text-align-right">
        <Button
          buttonStyle={ButtonStyle.SECONDARY_WHITE}
          size={ButtonSize.MEDIUM}
          text="Cancel"
          onClick={() => {
            props.onClose();
          }}
        />{' '}
        <SaveDatasetAssociationButton
          availableSources={props.availableSources}
          selectedSource={selectedSource}
          onSelectDataset={props.onSelectDataset}
          onClose={props.onClose}
        />
      </div>
      <AnalysisTemplateSourceTable
        sources={props.availableSources}
        setSelectedSource={setSelectedSource}
        initialSelectedSource={props.initialSelectedSource}
        analysisSourcesOrigin={props.analysisSourcesOrigin}
      />
    </div>
  );
}

interface ClosedDatasetEditButtonProps {
  selectedSource: string | null | undefined;
  availableSources: AnalysisSourcesForConfigOrExecution;
  isExpectedDataPresent: boolean;
  onOpenDatasetSelector: () => void;
  dataTestAttrs?: DataTestAttrs;
}

function ClosedDatasetEditButton(props: ClosedDatasetEditButtonProps): JSX.Element {
  if (props.selectedSource == null) {
    return (
      <Button
        buttonStyle={ButtonStyle.PRIMARY}
        size={ButtonSize.MEDIUM}
        text="Select a dataset"
        onClick={props.onOpenDatasetSelector}
        dataTestAttrs={props.dataTestAttrs}
      />
    );
  }

  const datasetName = getDatasetNameFromId(props.selectedSource, props.availableSources);

  return (
    <>
      <div
        className={classNames(
          css(!props.isExpectedDataPresent && styles.missingData),
          'u-display-inline-block u-mini-spacing-right'
        )}
      >
        {datasetName && <DatasetCellContent datasetName={datasetName} datasetId={props.selectedSource} />}
        {props.selectedSource != null && datasetName == null && (
          <div className={css(styles.missingSource)}>Missing source</div>
        )}
      </div>
      <Button
        onClick={props.onOpenDatasetSelector}
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.SECONDARY}
        text="Edit"
        dataTestAttrs={props.dataTestAttrs}
      />
    </>
  );
}

interface ColumnMappingsEditorProps {
  variable: AnalysisTemplateDataFrameVariableGraphQL;
  tableMappingConfig: SingleAnalysisTemplateTableMapping;
  availableSources: AnalysisSourcesForConfigOrExecution;
  onSetColumnMapping: (newMapping: Record<string, string>) => void;
}

function ColumnMappingsEditor(props: ColumnMappingsEditorProps): JSX.Element {
  const name = getDatasetNameFromId(props.tableMappingConfig.datasetId, props.availableSources) ?? 'Dataset';
  const columns =
    getColumnDefsFromDatasetId(props.tableMappingConfig.datasetId, props.availableSources) ?? [];

  // If we pass NULL to the input config, rather than an empty object, it will magically pre-fill the
  // column mappings. This is only useful (and only makes sense) for the initial config.
  const savedMapping = props.tableMappingConfig.columnMappings;
  const mappingForConfig = Object.keys(savedMapping).length === 0 ? null : savedMapping;

  return (
    <AnalysisTemplateInputTableConfig
      variable={props.variable}
      selectedInputTableInfo={{name, columns}}
      columnMapping={mappingForConfig}
      setColumnMapping={props.onSetColumnMapping}
    />
  );
}

interface SingleMappingCardProps {
  title: string;
  tableMappingConfig: SingleAnalysisTemplateTableMapping | null | undefined;
  variable: AnalysisTemplateDataFrameVariableGraphQL;
  availableSources: AnalysisSourcesForConfigOrExecution;
  analysisSourcesOrigin: AnalysisSourcesOrigin;
  onSelectDataset: (datasetId: string, datasetType: DatasetType) => void;
  onChangeColumnMappings: (datasetId: string, newMappings: Record<string, string>) => void;
}

export default function SingleMappingCard(props: SingleMappingCardProps): JSX.Element {
  const [isDatasetPickerOpen, setIsDatasetPickerOpen] = useState(false);
  const datasetId = props.tableMappingConfig?.datasetId;
  const hasDatasetBeenSelected = datasetId != null;
  const isExpectedDataPresent =
    datasetId != null &&
    isDataPresentForSourceDatasetId(datasetId, props.availableSources, props.analysisSourcesOrigin);
  const showMappingsEditor = !isDatasetPickerOpen && datasetId != null;

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({
        component: 'SingleMappingCard',
        element: 'root',
        key: props.variable.id,
      })}
    >
      <div className={css(styles.title)}>{props.title}</div>
      <div className="u-lr-split">
        <div className={css(styles.label)}>
          {isExpectedDataPresent && (
            <BenchlingIcon icon="check-circle" className={css(styles.successIcon, styles.icon)} />
          )}
          {/* Only show missing dataset icon if table mapping was previously configured or we're in execution.
           * If this is the first time the user is doing the mapping and simply hasn't mapped anything yet,
           * don't show any icons. */}
          {!isExpectedDataPresent &&
            (hasDatasetBeenSelected || props.analysisSourcesOrigin === AnalysisSourcesOrigin.EXECUTION) && (
              <BenchlingIcon icon="circle-xmark" className={css(styles.icon, styles.missingData)} />
            )}
          Dataset to use
        </div>
        <div>
          {!isDatasetPickerOpen && (
            <ClosedDatasetEditButton
              selectedSource={datasetId}
              availableSources={props.availableSources}
              isExpectedDataPresent={isExpectedDataPresent}
              onOpenDatasetSelector={() => {
                setIsDatasetPickerOpen(true);
              }}
              dataTestAttrs={{
                component: 'SingleMappingCard',
                element: 'ClosedDatasetEditButton',
              }}
            />
          )}
        </div>
      </div>
      <div>
        {isDatasetPickerOpen && (
          <DatasetPicker
            availableSources={props.availableSources}
            initialSelectedSource={datasetId}
            onSelectDataset={props.onSelectDataset}
            onClose={() => {
              setIsDatasetPickerOpen(false);
            }}
            analysisSourcesOrigin={props.analysisSourcesOrigin}
          />
        )}
      </div>
      <hr className={css(styles.hr)} />
      <div className="u-lr-split">
        <div className={css(styles.label)}>Mappings</div>
        <div>{!showMappingsEditor && <em>Select a dataset to map columns</em>}</div>
      </div>
      {showMappingsEditor && (
        <ColumnMappingsEditor
          tableMappingConfig={props.tableMappingConfig!}
          variable={props.variable}
          availableSources={props.availableSources}
          onSetColumnMapping={(newMappings) => {
            props.onChangeColumnMappings(datasetId, newMappings);
          }}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '3px',
    padding: '1em',
    marginBottom: '1em',
  },
  title: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '14px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 'bold',
  },
  hr: {
    marginTop: 9,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: Color.GRAY_300,
  },
  icon: {
    height: 16,
    width: 16,
    marginRight: 8,
  },
  successIcon: {
    color: Color.GREEN_500,
  },
  missingData: {
    color: Color.RED_500,
  },
  missingSource: {
    fontStyle: 'italic',
  },
});
